import { useState, Fragment } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { templateRelatedTemplatesRelayFragment$key } from "@/__generated__/templateRelatedTemplatesRelayFragment.graphql.ts";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";

import { TemplateCard } from "./template-card-relay";

interface TemplateRelatedTemplatesProps {
    template: templateRelatedTemplatesRelayFragment$key;
}

const tabbedSectionHeading = css({
    "fontSize": "2.25rem",
    "lineHeight": "2.75rem",
    "fontWeight": 500,
    "letterSpacing": 0.3,
    "color": "#1d3059",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "margin": 0,
    "marginBlockEnd": 40,

    "@media (max-width: 47.99rem)": {
        fontSize: "1.5rem",
        lineHeight: "1.75rem"
    }
});

const relatedTemplatesStyles = css({
    "display": "none",
    "@media(min-width: 1130px)": {
        display: "flex",
        gap: "32px"
    }
});

const relatedTemplatesMobileStyles = css({
    "alignItems": "center",
    "display": "flex",
    "flexWrap": "wrap",
    "gap": "32px",
    "@media(min-width: 1130px)": {
        display: "none"
    }
});

const relatedSectionStyles = css({
    "marginBlockStart": 120,
    "marginInlineStart": "auto",
    "marginInlineEnd": "auto",
    "@media(max-width: 769px)": {
        marginBlockStart: "64px"
    }
});

const buttonLeft = css({
    "backgroundColor": "#ffffff",
    "display": "block",
    "boxSizing": "border-box",
    "insetInlineStart": 0,
    "insetBlockStart": "50%",
    "width": 32,
    "height": 32,
    "border": "none",
    "boxShadow": "1px 1px 6px 0 rgba(0,0,0,.2)",
    "transform": "translateY(-40%)",
    "borderRadius": "50%",
    "outline": "none",
    "cursor": "pointer",
    "color": "#0052cc",
    "lineHeight": 28,
    "zIndex": 1,
    "align-self": "center"
});

const buttonRight = css({
    "backgroundColor": "#ffffff",
    "boxSizing": "border-box",
    "display": "block",
    "insetInlineEnd": 0,
    "insetBlockStart": "50%",
    "width": 32,
    "height": 32,
    "border": "none",
    "boxShadow": "1px 1px 6px 0 rgba(0,0,0,.2)",
    "transform": "translateY(-40%)",
    "borderRadius": "50%",
    "outline": "none",
    "cursor": "pointer",
    "color": "#0052cc",
    "lineHeight": 28,
    "zIndex": 1,
    "align-self": "center"
});

const buttonLeftArrow = css({
    transform: "rotate(-45deg)",
    width: 12,
    height: 12,
    display: "inline-block",
    lineHeight: 0,
    position: "absolute",
    marginBlockStart: -6,
    marginInlineStart: -4,
    boxSizing: "border-box",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    fontSize: 16
});

const buttonRightArrow = css({
    transform: "rotate(135deg)",
    width: 12,
    height: 12,
    display: "inline-block",
    lineHeight: 0,
    position: "absolute",
    marginBlockStart: -6,
    marginInlineStart: -8,
    boxSizing: "border-box",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    fontSize: 16
});

const LeftArrow = ({
    onClick,
    label
}: {
    onClick?: () => void;
    label?: string;
}) => (
    <button css={buttonLeft} onClick={onClick} aria-label={label}>
        <span css={buttonLeftArrow} />
    </button>
);
const RightArrow = ({
    onClick,
    label
}: {
    onClick?: () => void;
    label?: string;
}) => (
    <button css={buttonRight} onClick={onClick} aria-label={label}>
        <span css={buttonRightArrow} />
    </button>
);

const TemplateRelatedTemplatesRelayFragment = graphql`
    fragment templateRelatedTemplatesRelayFragment on ContentPlatformTemplate {
        relatedTemplates {
            templateId
            ...templateCardRelayFragment
        }
    }
`;

export const TemplateRelatedTemplates = ({
    template
}: TemplateRelatedTemplatesProps) => {
    const { relatedTemplates } = useFragment(
        TemplateRelatedTemplatesRelayFragment,
        template
    );
    const intl = useIntl();
    const previousLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselPreviousLabel
    );
    const nextLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselNextLabel
    );

    const [currentIndex, setCurrentIndex] = useState(0);

    const hasRelatedTemplates = relatedTemplates && relatedTemplates.length > 0;

    return (
        <section css={relatedSectionStyles}>
            <h2 css={tabbedSectionHeading}>
                <FormattedMessage
                    {...ConfluenceTemplateMessages.relatedTemplatesTitle}
                />
            </h2>
            {hasRelatedTemplates && (
                <>
                    <div css={relatedTemplatesStyles}>
                        {currentIndex !== 0 && (
                            <LeftArrow
                                label={previousLabel}
                                onClick={() => {
                                    setCurrentIndex(
                                        (prevIndex) =>
                                            (prevIndex -
                                                1 +
                                                relatedTemplates.length) %
                                            relatedTemplates.length
                                    );
                                }}
                            />
                        )}
                        {relatedTemplates
                            .slice(currentIndex, currentIndex + 2)
                            .map((relatedTemplate) => (
                                <Fragment key={relatedTemplate.templateId}>
                                    <TemplateCard template={relatedTemplate} />
                                </Fragment>
                            ))}
                        {currentIndex + 2 < relatedTemplates.length && (
                            <RightArrow
                                label={nextLabel}
                                onClick={() => {
                                    setCurrentIndex(
                                        (prevIndex) =>
                                            (prevIndex + 1) %
                                            relatedTemplates?.length
                                    );
                                }}
                            />
                        )}
                    </div>
                    <div css={relatedTemplatesMobileStyles}>
                        {relatedTemplates.map((relatedTemplate) => (
                            <Fragment key={relatedTemplate.templateId}>
                                <TemplateCard template={relatedTemplate} />
                            </Fragment>
                        ))}
                    </div>
                </>
            )}
        </section>
    );
};
